import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { getConsent, setConsent, initAnalytics, analyticsConfigured } from "../lib/analytics";
import { useLp } from "../i18n/lang";
import { useCopy } from "../i18n/copy";

const btnBase = "text-xs font-bold px-5 py-2.5 rounded-sm transition-colors";

export const CookieConsent = () => {
  const [banner, setBanner] = useState(false);
  const [panel, setPanel] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(false);
  const c = useCopy("cookies");
  const lp = useLp();

  useEffect(() => {
    const consent = getConsent();
    if (consent?.analytics && analyticsConfigured) initAnalytics();
    if (consent === null && analyticsConfigured) setBanner(true);
    const open = () => {
      setAnalyticsOn(Boolean(getConsent()?.analytics));
      setBanner(false);
      setPanel(true);
    };
    window.addEventListener("mf:open-cookie-settings", open);
    return () => window.removeEventListener("mf:open-cookie-settings", open);
  }, []);

  const choose = (value) => {
    setConsent(value);
    setBanner(false);
    setPanel(false);
  };

  if (!banner && !panel) return null;

  return (
    <>
      {banner && (
        <div role="dialog" aria-label={c.bannerAria} data-testid="cookie-banner"
             className="fixed bottom-0 inset-x-0 z-50 bg-obsidian text-slate-300 border-t border-[#41705F]">
          <div className="container-site py-5">
            <p className="text-xs leading-relaxed max-w-3xl">
              {c.bannerA}{" "}
              <Link to={lp("/politica-de-cookies/")} className="underline decoration-slate-500 underline-offset-2 hover:text-paper">
                {c.bannerLink}
              </Link>.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <button data-testid="cookie-accept-btn" onClick={() => choose(true)}
                      className={`${btnBase} bg-[#C56D42] text-white hover:bg-[#B45D3A]`}>
                {c.accept}
              </button>
              <button data-testid="cookie-reject-btn" onClick={() => choose(false)}
                      className={`${btnBase} bg-paper text-ink hover:bg-white`}>
                {c.reject}
              </button>
              <button data-testid="cookie-configure-btn" onClick={() => { setAnalyticsOn(false); setBanner(false); setPanel(true); }}
                      className={`${btnBase} border border-slate-400 text-slate-200 hover:border-paper`}>
                {c.configure}
              </button>
            </div>
          </div>
        </div>
      )}

      {panel && (
        <div role="dialog" aria-modal="true" aria-label={c.panelAria} data-testid="cookie-panel"
             className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-ink/50 p-4"
             onClick={(e) => { if (e.target === e.currentTarget) setPanel(false); }}>
          <div className="bg-paper w-full max-w-lg rounded-sm border hairline shadow-xl">
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b hairline">
              <h2 className="text-lg font-semibold text-ink">{c.panelTitle}</h2>
              <button onClick={() => setPanel(false)} aria-label={c.close} data-testid="cookie-panel-close"
                      className="p-1.5 text-slate-500 hover:text-ink transition-colors">
                <X size={18} />
              </button>
            </div>
            <div className="px-6 py-5 space-y-5 max-h-[55vh] overflow-y-auto">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-ink">{c.techT}</h3>
                  <p className="mt-1 text-xs text-slate-600 leading-relaxed">{c.techText}</p>
                </div>
                <span className="text-[10px] font-mono uppercase tracking-wider text-precision shrink-0 mt-1">{c.alwaysOn}</span>
              </div>
              <div className="flex items-start justify-between gap-4 border-t hairline pt-5">
                <div>
                  <h3 className="text-sm font-semibold text-ink">{c.analyticsT}</h3>
                  <p className="mt-1 text-xs text-slate-600 leading-relaxed">
                    {c.analyticsText}
                    {!analyticsConfigured && (
                      <strong className="block mt-1 text-ink">{c.analyticsOff}</strong>
                    )}
                  </p>
                </div>
                <label className="shrink-0 mt-1 flex items-center gap-2 text-xs text-slate-600">
                  <input type="checkbox" checked={analyticsOn} disabled={!analyticsConfigured}
                         onChange={(e) => setAnalyticsOn(e.target.checked)}
                         data-testid="cookie-analytics-toggle"
                         className="h-4 w-4 accent-[#183F3A] disabled:opacity-40" />
                  {c.enable}
                </label>
              </div>
            </div>
            <div className="px-6 py-4 border-t hairline flex flex-wrap gap-3">
              <button data-testid="cookie-save-btn" onClick={() => choose(analyticsOn && analyticsConfigured)}
                      className={`${btnBase} bg-[#C56D42] text-white hover:bg-[#B45D3A]`}>
                {c.save}
              </button>
              <button data-testid="cookie-panel-reject-btn" onClick={() => choose(false)}
                      className={`${btnBase} bg-paper-tint text-ink hover:bg-slate-200`}>
                {c.rejectAll}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
